import * as React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useForm } from 'react-hook-form';

import { Button, Loader, Grid, Cell, Radio } from '@teamsnap/teamsnap-ui';

import Modal from '../../components/common/modal';
import FormElementHook, { useFormDefaults } from '../../components/form/form-element-hook';
import { DateService, DateFormat } from 'frontend-toolkit';
import { getSportName } from '../../components/form/sport-select';
import * as CreateSeason from '../../core/gql/mutations/create-season';
import * as UpdateSeason from '../../core/gql/mutations/update-season';
import * as QuerySeasons from '../../core/gql/queries/seasons';
import * as ProgramSeasonsDivisions from 'core/gql/queries/program-season-divisions';
import * as ShowSeason from '../../core/gql/queries/show-season';
import * as QueryUserOrganizations from '../../core/gql/queries/user-organizations';

import { GqlSeason } from '../../core/gql/fragments/season';
import { GqlProgramExpanded } from '../../core/gql/fragments/program';
import { ActivitySeasonType } from '../../models/season';
import { isQueryFinished } from '../../core/apollo-helpers';
import { reactiveFeedbackMessage } from '../../core/apollo-cache';
import Feedback from '../../components/common/feedback';

import './season-edit.scss';

const LIMIT_DIVISIONS_NUMBER = 100;

const CreateElement = FormElementHook as new () => FormElementHook;

const divisionAttributeDescription = (selectedAttributes) => {
  if (!selectedAttributes || selectedAttributes.length < 1) {
    return null;
  }

  return selectedAttributes.map((attr) => attr.name).join(', ');
};

const ageDivisionAttributeDescription = (selectedAttributes) => {
  if (!selectedAttributes || selectedAttributes.length < 1) {
    return null;
  }

  if (selectedAttributes.length <= 2) {
    return selectedAttributes.map((attr) => attr.name).join(', ');
  } else {
    return `${selectedAttributes[0].name}...${selectedAttributes[selectedAttributes.length - 1].name}`;
  }
};

export interface Props {
  program: GqlProgramExpanded;
  activitySeasonType: ActivitySeasonType;
  selectedSeasonId?: number | null;
  isOpen: boolean;
  onClose: (showModal) => any;
  showArchived?: boolean;
}

const SeasonEdit: React.FunctionComponent<Props> = ({
  program,
  activitySeasonType,
  selectedSeasonId,
  isOpen,
  onClose,
  showArchived,
}) => {
  const mutationOptions = {
    refetchQueries: [
      { query: QuerySeasons.QUERY, variables: { programId: program.id, isArchived: showArchived ? null : false } },
      { query: ProgramSeasonsDivisions.QUERY, variables: { organizationId: program.organizationId } },
    ],
  };
  const [updateSeason, updateSeasonResponse] = useMutation(UpdateSeason.MUTATION, mutationOptions);
  const [createSeason, createSeasonResponse] = useMutation(CreateSeason.MUTATION, mutationOptions);

  const loadPreviousSeasonsResponse = useQuery(QuerySeasons.QUERY, {
    variables: { programId: program.id },
  });

  const seasonForm = useForm();
  const setupFormDefaults = React.useCallback((data) => {
    Object.entries({
      name: data?.showSeason?.name,
      startDate: data?.showSeason.startDate
        ? DateService.stringToDate(data?.showSeason.startDate, DateFormat.YYYY_MM_DD)
        : '',
      endDate: data?.showSeason.endDate
        ? DateService.stringToDate(data?.showSeason.endDate, DateFormat.YYYY_MM_DD)
        : '',
      copyDivisionStructure: true,
    }).forEach(([k, v]) => {
      seasonForm.setValue(k, v);
    });
  }, []);

  const showSeasonResponse = useQuery(ShowSeason.QUERY, {
    variables: { seasonId: selectedSeasonId },
    skip: selectedSeasonId == null,
    onCompleted: setupFormDefaults,
  });

  // Organization
  const userOrganizationsResponse = useQuery(QueryUserOrganizations.QUERY, {
    errorPolicy: 'all',
  });

  useFormDefaults(showSeasonResponse.data, setupFormDefaults, [showSeasonResponse.called, showSeasonResponse.loading]);

  const { register, handleSubmit, watch, setValue, getValues } = seasonForm;

  const copyDivisionStructureSelected = watch('copyDivisionStructure');
  // If copy division structure is deselected...
  if (!copyDivisionStructureSelected && getValues()['copyTeamStructure'] == true) {
    // then deselect copyTeamStructure
    setValue('copyTeamStructure', false);
  }

  const isEditing = !!selectedSeasonId;
  const isSubmitting = updateSeasonResponse.loading || createSeasonResponse.loading;

  const [isShowingSelectPreviousSeason, setIsShowingSelectPreviousSeason] = React.useState(false);

  const sportId = showSeasonResponse.data?.showSeason?.sportId || program.sportId;

  const areaOfOperation = showSeasonResponse.data?.showSeason?.areaOfOperation || program.areaOfOperation;
  let attributeValues = [getSportName(sportId), areaOfOperation?.name];

  if (!isEditing && !isShowingSelectPreviousSeason) {
    const genderDescription = divisionAttributeDescription(program.competitionGenders);
    if (genderDescription) {
      attributeValues = [...attributeValues, genderDescription];
    }

    const ageDivsionDescription = ageDivisionAttributeDescription(program.ageDivisions);
    if (ageDivsionDescription) {
      attributeValues = [...attributeValues, ageDivsionDescription];
    }

    const levelDescriptions = divisionAttributeDescription(program.competitionLevels);
    if (levelDescriptions) {
      attributeValues = [...attributeValues, levelDescriptions];
    }
  }

  const readyToShow = !showSeasonResponse.loading && isQueryFinished(userOrganizationsResponse);

  const initialDivisionsNumber =
    (program.competitionGenders.length || 1) *
    (program.ageDivisions.length || 1) *
    (program.competitionLevels.length || 1);
  const overDivisionsNumberLimit = initialDivisionsNumber >= LIMIT_DIVISIONS_NUMBER;
  const underDivisionsNumberLimit = initialDivisionsNumber < LIMIT_DIVISIONS_NUMBER;

  const submit = async (values) => {
    try {
      const organization = userOrganizationsResponse.data?.organizations?.find(
        (org) => org.id === program.organizationId
      );

      let feedbackMessage;
      const submitValues = {
        ...values,
        programId: program.id,
        startDate: DateService.dateToString(values.startDate, DateFormat.YYYY_MM_DD),
        endDate: DateService.dateToString(values.endDate, DateFormat.YYYY_MM_DD),
        country: organization.country,
        zipCode: organization.zipCode,
        timezone: organization.timezone,
        type: activitySeasonType,
        id: selectedSeasonId,
        sportId: parseInt(sportId),
        areaOfOperation: areaOfOperation?.id,
      };

      // Only send values to create divisions if they are under the LIMIT_DIVISIONS_NUMBER
      if (underDivisionsNumberLimit) {
        submitValues.ageDivisions = program.ageDivisions.map((tag) => tag.id);
        submitValues.competitionGenders = program.competitionGenders.map((tag) => tag.id);
        submitValues.competitionLevels = program.competitionLevels.map((tag) => tag.id);
      }

      if (submitValues.fromSeasonId) {
        submitValues.copyFromSeason = {
          seasonId: submitValues.fromSeasonId,
          copyDivisionStructure: submitValues.copyDivisionStructure,
          copyTeamStructure: submitValues.copyTeamStructure,
        };
      }

      if (selectedSeasonId) {
        await updateSeason({ variables: submitValues as UpdateSeason.Input });
        feedbackMessage = `The season ${submitValues.name} has been updated.`;
      } else {
        await createSeason({ variables: submitValues as CreateSeason.Input });
        feedbackMessage = `The season ${submitValues.name} has been created. Depending on the number, it may take a few minutes to create your divisions.`;
      }

      hide(true);
      reactiveFeedbackMessage({
        message: feedbackMessage,
        isError: false,
        autoclose: true,
      });
    } catch (e) {
      let errorMessage;

      if (/Owner doesn't exist/.test(e.message)) {
        errorMessage = 'Oops! The org owner needs to accept their invitation before seasons can be created.';
      } else {
        errorMessage = !selectedSeasonId
          ? 'Season create failed. Please try again.'
          : 'Season update failed. Please try again.';
      }

      reactiveFeedbackMessage({
        message: errorMessage,
        isError: true,
        autoclose: true,
      });
    }
  };

  const hide = (_updated?: boolean) => {
    setIsShowingSelectPreviousSeason(false);
    return onClose(false);
  };

  const showSeasonDropdown = (updated?: boolean) => {
    setIsShowingSelectPreviousSeason(!!updated);
  };

  const configureSeasonSelectData = (data) =>
    data.seasons
      .filter((e: GqlSeason) => e.type == activitySeasonType)
      .map((e: GqlSeason) => ({ text: e.name, value: e.id }));

  const showPreviousSeasons =
    !isEditing &&
    !loadPreviousSeasonsResponse.loading &&
    configureSeasonSelectData(loadPreviousSeasonsResponse.data).length > 0;

  return (
    <Modal isOpen={isOpen} onClose={() => onClose(false)}>
      {readyToShow && (
        <form onSubmit={handleSubmit(submit)}>
          <div className="page-content u-xs-padTopLg u-xs-padRightLg u-xs-padLeftLg" data-testid="editSeasonModal">
            <div>
              <div className="page-content-header Grid u-flexAlignItemsCenter">
                <div className="Grid-cell u-xs-size1of2 u-padBottomMd">
                  <h2 data-testid="editSeasonProgramHeader">{program.name}</h2>
                </div>
              </div>
              {!isEditing && overDivisionsNumberLimit && (
                <div className="u-spaceBottomXl" data-testid="negativeFeedback">
                  <Feedback type="negative" title="Uh Oh!">
                    {`Based on your program attributes, it looks like this season will have over ${LIMIT_DIVISIONS_NUMBER} divisions and that's a bit
                    too many to automatically create. You can either adjust the program attributes before creating the season
                    so there are fewer divisions, or you can continue to create this season but will need to manually add divisions later.`}
                    {/* eslint-disable-next-line */}
                    <a
                      href="https://helpme.teamsnap.com/article/1705-how-program-attributes-are-used"
                      target="_blank"
                      rel="noreferrer"
                      data-testid="learnMoreLink"
                    >
                      {' '}
                      Learn More.
                    </a>
                  </Feedback>
                </div>
              )}
              <Cell mods="u-size12of12">
                {attributeValues.map(
                  (attr) =>
                    attr && (
                      <span className="attribute-pill" key={attr} data-testid="greyTags">
                        {attr}
                      </span>
                    )
                )}
              </Cell>

              <Cell mods="u-size12of12">
                <CreateElement
                  label="Season Name"
                  useHookResponse={seasonForm}
                  inputRef={register({
                    required: 'You must enter a name for the season.',
                  })}
                  field="name"
                  type="text"
                  placeholder="Season Name (e.g. Fall 2021)"
                  data-testid="nameMessage"
                />
              </Cell>

              <Grid isWithGutter={true}>
                <Cell mods="u-size6of12">
                  <CreateElement
                    label="Start Date"
                    useHookResponse={seasonForm}
                    inputRef={register({
                      required: false,
                    })}
                    field="startDate"
                    type="date"
                    placeholder="TBD"
                    data-testid="startDateMessage"
                  />
                </Cell>
                <Cell mods="u-size6of12">
                  <CreateElement
                    label="End Date"
                    useHookResponse={seasonForm}
                    inputRef={register({
                      required: false,
                    })}
                    field="endDate"
                    type="date"
                    placeholder="TBD"
                    data-testid="endDateMessage"
                  />
                </Cell>
              </Grid>

              {showPreviousSeasons && (
                <Grid>
                  <Cell mods="u-size12of12">
                    <h4 className="FieldGroup-label u-padTopMd u-padBottomSm" data-testid="seasonInfoHeader">
                      Season Information:
                    </h4>
                    <div>
                      <Radio
                        mods="u-padBottomSm"
                        name="copy-dialog-true"
                        label="Copy information from a prior season"
                        group="copy-dialog"
                        inputProps={{
                          onChange: () => showSeasonDropdown(true),
                          'data-testid': 'copy-dialog-true',
                          checked: isShowingSelectPreviousSeason,
                        }}
                        testId="copyInfoRadioButton"
                      />

                      <small className="u-spaceLeftLg u-block" data-testid="copyDivisionMessage">
                        Copy the Division Structure, Reg Forms, Financial Settings and Season Settings from another
                        season within the selected program. Rosters, Communications, and Payment History will be cleared
                        and NOT carried over to the new season.
                      </small>
                    </div>

                    {isShowingSelectPreviousSeason && (
                      <>
                        <CreateElement
                          useHookResponse={seasonForm}
                          inputRef={register({
                            required: false,
                          })}
                          className="u-spaceLeftLg u-padTopMd"
                          field="fromSeasonId"
                          type="select"
                          placeholder="select a season"
                          options={configureSeasonSelectData(loadPreviousSeasonsResponse.data)}
                        />
                        <div className="u-spaceLeftLg u-padTopMd">
                          <CreateElement
                            useHookResponse={seasonForm}
                            inputRef={register({
                              required: false,
                            })}
                            field="copyDivisionStructure"
                            type="checkbox"
                            label="Copy Division Structure"
                            data-testid="copyDivisionCheckBox"
                          />
                          <CreateElement
                            className="u-spaceLeftLg u-padTopMd"
                            useHookResponse={seasonForm}
                            inputRef={register({
                              required: false,
                            })}
                            field="copyTeamStructure"
                            disabled={copyDivisionStructureSelected != true}
                            type="checkbox"
                            label="Copy Team Structure"
                            data-testid="copyTeamCheckBox"
                          />
                        </div>
                      </>
                    )}

                    <Radio
                      mods="u-padTopLg u-padBottomSm"
                      name="copy-dialog-false"
                      label="Start fresh for the new season"
                      group="copy-dialog"
                      inputProps={{
                        onChange: () => showSeasonDropdown(false),
                        checked: isShowingSelectPreviousSeason == false,
                      }}
                      testId="startFreshRadio"
                    />
                  </Cell>
                </Grid>
              )}
              <Cell mods="u-textLeft u-padTopXl u-padBottomXl u-spaceBottomXl">
                <div className="button-list">
                  <Button mods="Button--negative" icon="dismiss" onClick={hide} testId="modalCancelButton">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isDisabled={isSubmitting}
                    color="primary"
                    icon={isSubmitting ? null : 'check'}
                    testId="modalUpdateSeasonButton"
                  >
                    {isSubmitting && <Loader type="spin" style={{ width: '13px', height: '13px' }} />}
                    {isEditing ? 'Update' : 'Create'} Season
                  </Button>
                </div>
              </Cell>
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
};

SeasonEdit.defaultProps = {
  showArchived: null,
};

export default SeasonEdit;
